import React from "react";
import DynamicFormCard from "../forms/DynamicFormCard";
import ReviewTaskCard from "../forms/ReviewTaskCard";
import CreatedFileCard from "../files/CreatedFileCard";
import {
  normalizeChatUiPolicy,
  parseHumanTakeoverMarker,
  parseContextCompactedMarker,
  isDynamicFormTool,
  isCreatedFileTool,
  parseSupervisorDecisionContent,
  isAgentBusyStatus,
  resolveUserSenderDisplayName,
} from "./conversationUtils";
import {
  SystemMessage,
  HumanTakeoverMarker,
  ContextCompactedMarker,
  FormSubmissionMessage,
  parseValidationResult,
  ValidationResultBubble,
  validationPartToResult,
} from "./SystemMarkers";
import {
  WorkflowMessage,
  WorkflowResponseMessage,
  InterAgentMessage,
} from "./InterAgentMessages";
import {
  SendMessageCard,
  UserMessage,
  AgentMessage,
  AgentOutputCard,
  parseAgentOutputJson,
  SupervisorDecisionMessage,
} from "./MessageBubbles";
import {
  ScheduleMessageCard,
  RecurringScheduleCard,
  ToolCallCard,
} from "./ToolCards";
import SpawnWorkerCard from "./SpawnWorkerCard";
import AskHumanCard from "./AskHumanCard";
import CredentialSetupCard from "./CredentialSetupCard";
import TeamActivityCard from "./TeamActivityCard";
import ArtifactToolCard, {
  canRenderArtifactToolCard,
} from "./ArtifactToolCard";
import ArtifactEventMessage from "./ArtifactEventMessage";
import {
  DateDivider,
  ReasoningBlock,
  StepDivider,
  StoppedIndicator,
  ThinkingIndicator,
} from "./ActivityIndicators";

export function renderTypedAssistantParts(typedMessage, keyPrefix, opts = {}) {
  const {
    agentName,
    agentId,
    grouped,
    onOpenAgentSettings,
    onOpenDynamicForm,
    onOpenCreatedFile,
    showToolCallsAndOutputsInChat,
    showSchedulingActivityInChat,
    showInterAgentMessagesInChat,
    respondedCorrelationIds,
    scheduledMessages,
    onEditScheduledMessage,
    onCancelScheduledMessage,
    onRefreshScheduledMessages,
    compactToolCalls,
    workerStatusMap,
    session,
    chatSessionId,
    onSubmitAskHumanAnswer,
    onSubmitCredentialSetup,
  } = opts;
  const elements = [];
  const parts = typedMessage.parts || [];
  for (let p = 0; p < parts.length; p++) {
    const part = parts[p];
    const partKey = `${keyPrefix}-part-${p}`;
    // A hidden part (validation-rejected attempt under hideFromChat) is never rendered.
    if (part.hidden) continue;
    if (part.type === "step-start") {
      // Divider between iterations of one turn. Suppressed when it would be the
      // first thing in the message (leading divider) or has no visible content
      // after it (trailing divider) — e.g. once prior steps are hidden.
      const hasLaterContent = parts
        .slice(p + 1)
        .some((later) => later && !later.hidden && later.type !== "step-start");
      if (elements.length === 0 || !hasLaterContent) continue;
      elements.push(<StepDivider key={partKey} />);
      continue;
    }
    if (part.type === "validation") {
      elements.push(
        <ValidationResultBubble
          key={partKey}
          result={validationPartToResult(part)}
          timestamp={typedMessage.createdAt || typedMessage.updatedAt}
        />
      );
    } else if (part.type === "reasoning") {
      const reasoningContent = part.text || "";
      if (!reasoningContent && part.state !== "streaming") continue;
      elements.push(
        <ReasoningBlock
          key={partKey}
          content={reasoningContent}
          state={part.state}
          startedAt={part.startedAt}
          endedAt={part.endedAt}
          agentName={agentName}
          agentId={agentId}
          grouped={p > 0}
          onOpenAgentSettings={onOpenAgentSettings}
        />
      );
    } else if (part.type === "text") {
      const textContent = part.text || "";
      if (!textContent) continue;
      if (part.state !== "streaming" && parseAgentOutputJson(textContent)) {
        elements.push(
          <AgentOutputCard
            key={partKey}
            content={textContent}
            agentName={agentName}
            agentId={agentId}
            timestamp={typedMessage.createdAt || typedMessage.updatedAt}
            grouped={p > 0}
            onOpenAgentSettings={onOpenAgentSettings}
            workflowSessionId={session?.workflowSessionId}
            projectId={session?.projectId}
            projectSessionMode={session?.projectSessionMode}
          />
        );
      } else {
        elements.push(
          <AgentMessage
            key={partKey}
            content={textContent}
            agentName={agentName}
            agentId={agentId}
            timestamp={typedMessage.createdAt || typedMessage.updatedAt}
            grouped={p > 0}
            isStreaming={part.state === "streaming"}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      }
    } else if (part.type === "tool") {
      const toolName = part.toolName || "";
      if (
        !showToolCallsAndOutputsInChat &&
        !isDynamicFormTool(toolName) &&
        !isCreatedFileTool(toolName) &&
        toolName !== "request_decision" &&
        toolName !== "spawn_worker" &&
        toolName !== "review_task" &&
        toolName !== "ask_human" &&
        toolName !== "request_credential_setup" &&
        toolName !== "create_artifact"
      )
        continue;
      if (
        !showSchedulingActivityInChat &&
        (toolName === "schedule_message" || toolName === "recurring_schedule")
      )
        continue;
      if (!showInterAgentMessagesInChat && toolName === "send_message")
        continue;
      let parsedInput = part.input;
      if (typeof parsedInput === "string") {
        try {
          parsedInput = JSON.parse(parsedInput);
        } catch (_) {}
      }
      const inputStr =
        typeof parsedInput === "object" && parsedInput !== null
          ? JSON.stringify(parsedInput, null, 2)
          : part.inputText || "";
      const resultStr =
        part.output !== undefined && part.output !== null
          ? typeof part.output === "string"
            ? part.output
            : JSON.stringify(part.output, null, 2)
          : part.errorText || null;
      const lifecycleState = part.state;

      if (isDynamicFormTool(toolName)) {
        elements.push(
          <DynamicFormCard
            key={partKey}
            toolName={toolName}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            onOpenForm={onOpenDynamicForm}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
          />
        );
      } else if (toolName === "review_task") {
        elements.push(
          <ReviewTaskCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            onOpenForm={onOpenDynamicForm}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
          />
        );
      } else if (isCreatedFileTool(toolName)) {
        elements.push(
          <CreatedFileCard
            key={partKey}
            toolName={toolName}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            onOpenFile={onOpenCreatedFile}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
          />
        );
      } else if (toolName === "send_message") {
        let sendMsgResponseReq = null;
        let sendMsgCorrelationId = null;
        if (parsedInput && typeof parsedInput === "object") {
          sendMsgResponseReq = parsedInput.responseRequirement;
        }
        const outputObj = part.output;
        if (outputObj && typeof outputObj === "object") {
          if (!sendMsgResponseReq)
            sendMsgResponseReq = outputObj.responseRequirement;
          sendMsgCorrelationId = outputObj.correlationId;
        }
        const awaitingResponse =
          sendMsgResponseReq === "REQUIRED" &&
          sendMsgCorrelationId &&
          respondedCorrelationIds &&
          !respondedCorrelationIds.has(sendMsgCorrelationId);
        elements.push(
          <SendMessageCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            awaitingResponse={awaitingResponse}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
          />
        );
      } else if (toolName === "schedule_message") {
        elements.push(
          <ScheduleMessageCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            timestamp={
              typedMessage.timestamp ||
              typedMessage.createdAt ||
              typedMessage.updatedAt
            }
            lifecycleState={lifecycleState}
            scheduledMessages={scheduledMessages}
            onEditScheduledMessage={onEditScheduledMessage}
            onCancelScheduledMessage={onCancelScheduledMessage}
            onRefreshScheduledMessages={onRefreshScheduledMessages}
          />
        );
      } else if (toolName === "recurring_schedule") {
        elements.push(
          <RecurringScheduleCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            lifecycleState={lifecycleState}
          />
        );
      } else if (toolName === "spawn_worker") {
        let workerLiveStatus = null;
        if (workerStatusMap) {
          try {
            const parsed =
              typeof resultStr === "string" ? JSON.parse(resultStr) : resultStr;
            if (
              parsed?.workerSessionId &&
              workerStatusMap[parsed.workerSessionId]
            ) {
              workerLiveStatus = workerStatusMap[parsed.workerSessionId].status;
            }
          } catch {}
        }
        elements.push(
          <SpawnWorkerCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
            compact={compactToolCalls}
            liveWorkerStatus={workerLiveStatus}
          />
        );
      } else if (toolName === "ask_human") {
        elements.push(
          <AskHumanCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            lifecycleState={lifecycleState}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            chatSessionId={chatSessionId}
            onSubmitAnswer={onSubmitAskHumanAnswer}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      } else if (toolName === "request_credential_setup") {
        elements.push(
          <CredentialSetupCard
            key={partKey}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            chatSessionId={chatSessionId}
            onSubmitCredentialSetup={onSubmitCredentialSetup}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      } else if (toolName === "create_artifact") {
        if (canRenderArtifactToolCard(resultStr)) {
          elements.push(
            <ArtifactToolCard
              key={partKey}
              args={inputStr}
              result={resultStr}
              agentName={agentName}
              agentId={agentId}
              grouped={false}
              onOpenAgentSettings={onOpenAgentSettings}
            />
          );
        } else {
          elements.push(
            <ToolCallCard
              key={partKey}
              toolName={toolName}
              args={inputStr}
              result={resultStr}
              agentName={agentName}
              agentId={agentId}
              grouped={false}
              onOpenAgentSettings={onOpenAgentSettings}
              lifecycleState={lifecycleState}
              compact={compactToolCalls}
            />
          );
        }
      } else {
        elements.push(
          <ToolCallCard
            key={partKey}
            toolName={toolName}
            args={inputStr}
            result={resultStr}
            agentName={agentName}
            agentId={agentId}
            grouped={false}
            onOpenAgentSettings={onOpenAgentSettings}
            lifecycleState={lifecycleState}
            compact={compactToolCalls}
          />
        );
      }
    }
  }
  return elements;
}

function hasVisibleActiveStreamPart(messages) {
  if (!Array.isArray(messages)) return false;
  return messages.some((msg) => {
    if (msg?.role !== "assistant" || msg.status !== "streaming" || !Array.isArray(msg.parts)) {
      return false;
    }
    return msg.parts.some((part) => {
      if (part.type === "reasoning" || part.type === "text") {
        return part.state === "streaming" || Boolean(part.text);
      }
      if (part.type === "tool") {
        return part.state === "input-streaming" || part.state === "input-available";
      }
      return false;
    });
  });
}

export function buildConversationMessages({
  messages,
  session,
  defaultAgentId,
  defaultAgentName,
  chatUiPolicy,
  scheduledMessages = null,
  onEditScheduledMessage = null,
  onCancelScheduledMessage = null,
  onRefreshScheduledMessages = null,
  onOpenDynamicForm = null,
  onOpenCreatedFile = null,
  onOpenAgentSettings = null,
  chatStatus = "ready",
  streamToolLabel = null,
  workerStatusMap = null,
  teamActivityByRun = null,
  viewerName = "",
  viewerDisplayName = "",
  onSubmitAskHumanAnswer = null,
  onSubmitCredentialSetup = null,
}) {
  if (!Array.isArray(messages)) {
    return { rendered: [], lastRole: null, lastSenderKey: null };
  }

  // Team member activity cards: a settled run's card renders immediately before
  // its final projected message (matched by providerMetadata.teamRunId); still
  // running runs (or settled runs whose final message hasn't projected yet) trail
  // at the end. Each run is placed exactly once, so the same node running again in
  // a later turn produces a distinct card rather than reusing the previous one.
  const teamEntriesByRun = {};
  if (teamActivityByRun) {
    for (const entry of Object.values(teamActivityByRun)) {
      if (entry?.runId) teamEntriesByRun[entry.runId] = entry;
    }
  }
  const placedTeamRunIds = new Set();

  const rendered = [];
  let prevRole = null;
  let prevSenderKey = null;
  let lastDateKey = null;
  let firstUserMessageSeen = false;
  const isWorkflowSource =
    session?.source === "WORKFLOW_INVOCATION" ||
    session?.source === "WORKFLOW_INVOCATION_ASYNC";
  const {
    showSystemPromptInChat,
    showToolCallsAndOutputsInChat,
    showInterAgentMessagesInChat,
    showWorkflowProvenanceInChat,
    showSchedulingActivityInChat,
    showSupervisorControlMessagesInChat,
    forceUserNameAsYou,
    compactToolCalls,
    publicUserDisplayName,
  } = normalizeChatUiPolicy(chatUiPolicy);
  const useWorkflowProvenanceCards =
    isWorkflowSource && showWorkflowProvenanceInChat;

  const respondedCorrelationIds = new Set();
  for (const msg of messages) {
    if (msg.role === "user" && msg.senderAgentId && msg.correlationId) {
      respondedCorrelationIds.add(msg.correlationId);
    }
  }

  const maybeInsertDateDivider = (timestamp, keyPrefix) => {
    if (!timestamp) return;
    const dateKey = new Date(timestamp).toDateString();
    if (dateKey !== lastDateKey) {
      rendered.push(
        <DateDivider key={`date-${keyPrefix}`} dateStr={timestamp} />
      );
      lastDateKey = dateKey;
      prevRole = null;
      prevSenderKey = null;
    }
  };

  const isInternalRuntimeSystemLine = (line) => {
    const trimmed = String(line || "").trim();
    if (!trimmed) return false;
    return (
      trimmed.startsWith("Current UTC time:") ||
      trimmed.startsWith("The user's timezone is ") ||
      trimmed ===
        "When scheduling messages at specific times, convert to the user's timezone offset in ISO-8601 format."
    );
  };

  const sanitizeSystemPromptForDisplay = (content) => {
    if (typeof content !== "string") return "";
    const lines = content.split(/\r?\n/);
    const filtered = lines.filter((line) => !isInternalRuntimeSystemLine(line));
    const output = [];
    let prevBlank = false;
    for (const line of filtered) {
      const blank = !String(line || "").trim();
      if (blank && prevBlank) continue;
      output.push(line);
      prevBlank = blank;
    }
    return output.join("\n").trim();
  };

  const isSystemAuthoredUserMessage = (msg) => {
    if (msg?.role !== "user") return false;
    const senderName = msg.senderDisplayName || msg.senderName || "";
    return (
      senderName === "System" ||
      msg.senderType === "SYSTEM" ||
      msg.messageChannel === "SYSTEM"
    );
  };

  // Place a team member activity card immediately before the assistant message that carries its
  // final projected output (matched by run id), exactly once per run. Prefer the live entry
  // (streaming/just-finished); on cold load there is none, so reconstruct a settled card from the
  // message metadata so the card persists instead of vanishing. Pre-runId messages fall back to
  // the child session id as a stable per-message key. Does NOT consume the message — the assistant
  // bubble still renders normally afterwards.
  const placeTeamActivityCard = (msg, msgKey) => {
    if (msg.role !== "assistant") return;
    const childSessionId = msg.providerMetadata?.teamChildSessionId;
    if (!childSessionId) return;
    const runId = msg.providerMetadata?.teamRunId || childSessionId;
    if (placedTeamRunIds.has(runId)) return;

    const liveEntry = teamEntriesByRun[runId];
    const fallbackEntry = liveEntry
      ? null
      : {
          runId,
          nodeId: msg.providerMetadata?.nodeId || null,
          agentId: msg.sourceAgentId || msg.senderAgentId || null,
          agentName:
            msg.sourceAgentName ||
            msg.senderName ||
            msg.providerMetadata?.label ||
            null,
          childSessionId,
          label: msg.providerMetadata?.label || null,
          status: "done",
        };
    maybeInsertDateDivider(msg.timestamp || msg.createdAt, `teamcard-${msgKey}`);
    rendered.push(
      <TeamActivityCard
        key={`teamcard-${runId}`}
        runId={runId}
        fallbackEntry={fallbackEntry}
        grouped={false}
        onOpenAgentSettings={onOpenAgentSettings}
        onSubmitAskHumanAnswer={onSubmitAskHumanAnswer}
      />
    );
    placedTeamRunIds.add(runId);
    prevRole = null;
    prevSenderKey = null;
  };

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    const msgKey = msg.messageId || msg.id || `idx-${i}`;
    const assistantName =
      msg.sourceAgentName || msg.agentName || defaultAgentName || "Agent";
    const assistantId =
      msg.sourceAgentId || msg.agentId || defaultAgentId || null;
    const assistantSenderKey = assistantId || assistantName || "__assistant__";
    const systemAuthoredUserMessage = isSystemAuthoredUserMessage(msg);
    const userName = systemAuthoredUserMessage
      ? "System"
      : resolveUserSenderDisplayName(msg, {
          forceUserNameAsYou,
          publicUserDisplayName,
          viewerName,
          viewerDisplayName,
        });
    const userSenderKey =
      msg.senderAgentId ||
      msg.senderUserId ||
      msg.senderId ||
      userName ||
      "__user__";

    placeTeamActivityCard(msg, msgKey);

    // A card-only anchor (persisted for a thinking/hidden node that streamed an activity card but
    // has no inline bubble) is consumed entirely by placeTeamActivityCard above — never render its
    // empty assistant bubble.
    if (msg.providerMetadata?.teamActivityCardOnly) {
      continue;
    }

    if (msg.role === "system") {
      // The human's answer to an ask_human question is persisted as an audit-only system message.
      // It is already surfaced by the ask_human card's answered state, so never render it as a
      // standalone "System prompt" block — regardless of visibility flags or the
      // show-system-prompt toggle (also hides legacy answers persisted before the visibility fix).
      if (msg.messageChannel === "HUMAN_ANSWER") {
        continue;
      }
      const humanTakeoverMarker = parseHumanTakeoverMarker(msg.content);
      if (humanTakeoverMarker) {
        maybeInsertDateDivider(msg.timestamp, `takeover-${msgKey}`);
        rendered.push(
          <HumanTakeoverMarker
            key={`msg-${msgKey}`}
            marker={humanTakeoverMarker}
            timestamp={msg.timestamp}
          />
        );
        prevRole = "system";
        prevSenderKey = null;
        continue;
      }
      const contextCompactedMarker = parseContextCompactedMarker(msg.content);
      if (contextCompactedMarker) {
        maybeInsertDateDivider(msg.timestamp, `compacted-${msgKey}`);
        rendered.push(
          <ContextCompactedMarker
            key={`msg-${msgKey}`}
            marker={contextCompactedMarker}
            timestamp={msg.timestamp}
          />
        );
        prevRole = "system";
        prevSenderKey = null;
        continue;
      }
      const validationResult = parseValidationResult(msg.content);
      if (validationResult) {
        maybeInsertDateDivider(msg.timestamp, `validation-${msgKey}`);
        rendered.push(
          <ValidationResultBubble
            key={`msg-${msgKey}`}
            result={validationResult}
            timestamp={msg.timestamp}
          />
        );
        prevRole = "system";
        prevSenderKey = null;
        continue;
      }
      if (
        msg.messageChannel === "FORM_SUBMISSION" ||
        msg.messageChannel === "REVIEW_SUBMISSION"
      ) {
        maybeInsertDateDivider(msg.timestamp, `form-${msgKey}`);
        rendered.push(
          <FormSubmissionMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
            senderName={msg.senderName || msg.senderDisplayName}
            isReview={msg.messageChannel === "REVIEW_SUBMISSION"}
          />
        );
        prevRole = "system";
        prevSenderKey = null;
        continue;
      }
      if (msg.messageChannel === "ARTIFACT_EVENT") {
        maybeInsertDateDivider(msg.timestamp, `artifact-${msgKey}`);
        rendered.push(
          <ArtifactEventMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
          />
        );
        prevRole = "system";
        prevSenderKey = null;
        continue;
      }
      if (!showSystemPromptInChat) {
        continue;
      }
      const systemContent = sanitizeSystemPromptForDisplay(msg.content);
      if (!systemContent) {
        continue;
      }
      rendered.push(
        <SystemMessage key={`msg-${msgKey}`} content={systemContent} />
      );
      prevRole = "system";
      prevSenderKey = null;
    } else if (msg.role === "user") {
      if (
        msg.senderName === "System" &&
        typeof msg.content === "string" &&
        msg.content.startsWith("[Worker Completion Report]")
      ) {
        continue;
      }
      if (
        typeof msg.content === "string" &&
        msg.content.startsWith("Your previous output did not pass validation.")
      ) {
        continue;
      }
      maybeInsertDateDivider(msg.timestamp, msgKey);
      if (msg.senderAgentId) {
        if (!showInterAgentMessagesInChat) {
          continue;
        }
        rendered.push(
          <InterAgentMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
            senderAgentId={msg.senderAgentId}
            senderAgentName={msg.senderAgentName}
            senderAgentSessionId={msg.senderAgentSessionId}
            responseRequirement={msg.responseRequirement}
            correlationId={msg.correlationId}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
        prevRole = "user";
        prevSenderKey =
          msg.senderAgentId || msg.senderAgentName || "__agent_user__";
      } else if (useWorkflowProvenanceCards && !firstUserMessageSeen) {
        firstUserMessageSeen = true;
        rendered.push(
          <WorkflowMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
            projectId={session?.projectId}
            projectName={session?.projectName}
            workflowSessionId={session?.workflowSessionId}
          />
        );
        prevRole = "user";
        prevSenderKey = userSenderKey;
      } else {
        const grouped = prevRole === "user" && userSenderKey === prevSenderKey;
        const effectiveMessageChannel = msg.messageChannel || null;
        rendered.push(
          <UserMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            timestamp={msg.timestamp}
            grouped={grouped}
            senderName={userName}
            messageChannel={effectiveMessageChannel}
            imageUrls={msg.imageUrls}
          />
        );
        prevRole = "user";
        prevSenderKey = userSenderKey;
      }
    } else if (
      msg.role === "assistant" &&
      Array.isArray(msg.parts) &&
      msg.parts.length > 0
    ) {
      maybeInsertDateDivider(msg.timestamp || msg.createdAt, msgKey);
      const grouped =
        (prevRole === "assistant" || prevRole === "tool") &&
        prevSenderKey === assistantSenderKey;
      const partElements = renderTypedAssistantParts(msg, `msg-${msgKey}`, {
        agentName: assistantName,
        agentId: assistantId,
        grouped,
        onOpenAgentSettings,
        onOpenDynamicForm,
        onOpenCreatedFile,
        showToolCallsAndOutputsInChat,
        showSchedulingActivityInChat,
        showInterAgentMessagesInChat,
        respondedCorrelationIds,
        scheduledMessages,
        onEditScheduledMessage,
        onCancelScheduledMessage,
        onRefreshScheduledMessages,
        compactToolCalls,
        workerStatusMap,
        session,
        chatSessionId: session?.chatSessionId,
        onSubmitAskHumanAnswer,
        onSubmitCredentialSetup,
      });
      rendered.push(...partElements);
      if (partElements.length > 0) {
        if (msg.interrupted) {
          rendered.push(<StoppedIndicator key={`stopped-${msgKey}`} />);
        }
        prevRole = "assistant";
        prevSenderKey = assistantSenderKey;
      }
    } else if (msg.role === "assistant" && msg.content) {
      maybeInsertDateDivider(msg.timestamp, msgKey);
      const grouped =
        (prevRole === "assistant" || prevRole === "tool") &&
        prevSenderKey === assistantSenderKey;
      const supervisorDecision = parseSupervisorDecisionContent(msg.content);
      if (supervisorDecision && !showSupervisorControlMessagesInChat) {
        const takeoverResponse = supervisorDecision.takeoverResponse?.trim();
        if (takeoverResponse) {
          rendered.push(
            <AgentMessage
              key={`msg-${msgKey}`}
              content={takeoverResponse}
              agentName={assistantName}
              agentId={assistantId}
              timestamp={msg.timestamp}
              grouped={grouped}
              onOpenAgentSettings={onOpenAgentSettings}
            />
          );
        } else {
          continue;
        }
      } else if (useWorkflowProvenanceCards) {
        rendered.push(
          <WorkflowResponseMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            agentName={assistantName}
            agentId={assistantId}
            timestamp={msg.timestamp}
            grouped={grouped}
            projectId={session?.projectId}
            projectName={session?.projectName}
            workflowSessionId={session?.workflowSessionId}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      } else if (supervisorDecision) {
        rendered.push(
          <SupervisorDecisionMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            agentName={assistantName}
            agentId={assistantId}
            timestamp={msg.timestamp}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      } else if (parseAgentOutputJson(msg.content)) {
        rendered.push(
          <AgentOutputCard
            key={`msg-${msgKey}`}
            content={msg.content}
            agentName={assistantName}
            agentId={assistantId}
            timestamp={msg.timestamp}
            grouped={grouped}
            onOpenAgentSettings={onOpenAgentSettings}
            workflowSessionId={session?.workflowSessionId}
            projectId={session?.projectId}
            projectSessionMode={session?.projectSessionMode}
          />
        );
      } else {
        rendered.push(
          <AgentMessage
            key={`msg-${msgKey}`}
            content={msg.content}
            agentName={assistantName}
            agentId={assistantId}
            timestamp={msg.timestamp}
            grouped={grouped}
            onOpenAgentSettings={onOpenAgentSettings}
          />
        );
      }
      if (msg.interrupted) {
        rendered.push(<StoppedIndicator key={`stopped-${msgKey}`} />);
      }
      prevRole = "assistant";
      prevSenderKey = assistantSenderKey;
    }
  }

  // Trailing cards: running runs, or settled runs whose final message has not
  // been projected yet. Ordered by start time so concurrent branches stay stable.
  let hasRunningTeamCard = false;
  if (teamActivityByRun) {
    const trailing = Object.values(teamActivityByRun)
      .filter((entry) => entry && entry.runId && !placedTeamRunIds.has(entry.runId))
      .sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0));
    for (const entry of trailing) {
      if (entry.status === "running") hasRunningTeamCard = true;
      rendered.push(
        <TeamActivityCard
          key={`teamcard-${entry.runId}`}
          runId={entry.runId}
          grouped={false}
          onOpenAgentSettings={onOpenAgentSettings}
          onSubmitAskHumanAnswer={onSubmitAskHumanAnswer}
        />
      );
      placedTeamRunIds.add(entry.runId);
    }
    prevRole = null;
    prevSenderKey = null;
  }

  const needsThinking =
    isAgentBusyStatus(chatStatus) &&
    !hasVisibleActiveStreamPart(messages) &&
    !hasRunningTeamCard;
  if (needsThinking) {
    rendered.push(
      <ThinkingIndicator
        key="__thinking__"
        agentName={defaultAgentName || "Agent"}
        agentId={defaultAgentId}
        onOpenAgentSettings={onOpenAgentSettings}
        label={streamToolLabel}
      />
    );
  }

  return {
    rendered,
    lastRole: prevRole,
    lastSenderKey: prevSenderKey,
    isShowingThinking: needsThinking,
  };
}
